// Admin and leadership (leadership: AEs only). Switches whose book the dashboard reads, via the view-as header.
//
// The list comes from GET /api/admin/strategists — every listed non-admin, each
// carrying their role — not from hardcoded names. Grouped by role because
// thirteen flat names is a worse control than the five it replaces.
// The selection lives in sessionStorage so a closed tab forgets it.

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { fetchViewAsPeople, readViewAs, viewAsSwitcherVisible, writeViewAs } from '../api/client';
import type { Viewer } from '../api/types';
import { useAuthContext } from '../auth/AuthContext';
import {
  firstNameFromEmail, groupViewAsPeople, viewAsSwitcherLabel,
} from '../lib/viewAs';

export function ViewAsSwitcher({ viewer }: { viewer: Viewer }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { getToken } = useAuthContext();
  const stored = readViewAs();
  const visible = viewAsSwitcherVisible(viewer.role);

  const listQuery = useQuery({
    queryKey: ['admin-view-as-people'],
    queryFn: () => fetchViewAsPeople(getToken),
    enabled: visible,
  });

  if (!visible) return null;

  const people = listQuery.data?.people ?? [];
  const current = stored ?? '';
  const known = people.some(row => row.email === current);
  const options = known || !current
    ? people
    : [...people, {
        email: current,
        name: firstNameFromEmail(current),
        role: viewer.acting_for ? (viewer.role ?? 'viewer') : 'viewer',
      }];
  const groups = groupViewAsPeople(options);
  const selected = options.find(row => row.email === current);

  function onChange(email: string) {
    writeViewAs(email || null);
    void queryClient.invalidateQueries();

    // An AE has one page and every other board refuses them, so viewing as one goes there, and
    // going back to yourself leaves it (GRO-360).
    const chosen = options.find(row => row.email === email);
    if (chosen?.role === 'ae') navigate('/ae');
    else if (location.pathname === '/ae') navigate('/');
  }

  return (
    <label className="viewas">
      Viewing as:{' '}
      <select
        aria-label={viewAsSwitcherLabel(selected)}
        value={current}
        onChange={event => onChange(event.target.value)}
      >
        <option value="">myself</option>
        {groups.map(group => (
          <optgroup key={group.role} label={group.label}>
            {group.people.map(row => (
              <option key={row.email} value={row.email}>{row.name}</option>
            ))}
          </optgroup>
        ))}
      </select>
      {selected ? <span> · {selected.role}</span> : null}
    </label>
  );
}
